import { Vec, type Vector } from "./vector";
import { operatorSkin } from "../definitions/operatorSkins";
import { jointTransform, operatorBodyPose, operatorFoot, RIG_IDENTITY, solveOperatorJoint, transformRigPoint, type RigTransform } from "./operatorKinematics";

export interface Point3 { x: number; y: number; z: number; }
export type RigGesture = "ready" | "fire" | "punch" | "reload" | "heal" | "throw" | "punchLeft";
export type OperatorCarry = "unarmed" | "oneHanded" | "twoHanded";
export interface OperatorRigState {
    rotation: number;
    phase: number;
    moving: number;
    moveAngle: number;
    gesture: RigGesture;
    gesturePhase: number;
    helmet: number;
    vest: number;
    backpack: number;
    skinId?: string;
    leftHand: Vector;
    rightHand: Vector;
    carry?: OperatorCarry;
    /** False only in the local renderer A/B diagnostic. */
    armMotion?: boolean;
    weaponBob?: number;
    /** Shared locomotion carry transform, also applied to authoritative poses. */
    runStride?: number;
    weaponLift?: number;
    weaponSway?: number;
    /** Stable client render detail; collision always keeps its original mesh. */
    compactArms?: boolean;
}
export interface RigSurface { points: Vector[]; depth: number; color: number; ink?: Vector[][]; depths?: number[]; lights?: number[]; pigment?: number; }
export interface RigPart { name: string; hull: Vector[]; surfaces: RigSurface[]; convex?: boolean; }

/** Optional per-actor geometry sink. Rendering and collision use separate workspaces
 * with this shared recipe; the default builder remains the reference implementation. */
export interface OperatorRigSink {
    begin(state: OperatorRigState): RigPart[];
    setTransform?(transform: RigTransform): void;
    mesh(name: string, vertices: Point3[], faces: number[][], color: number, ink: boolean, normals: Point3[] | undefined, convex: boolean): void;
    ellipsoid(name: string, center: Point3, size: Point3, color: number, segments: number, rings: number, phiEnd: number): void;
    box(name: string, center: Point3, size: Point3, color: number, bevel: number): void;
    limb(name: string, a: Point3, b: Point3, radius: number, color: number): void;
}

const p = (x: number, y: number, z: number): Point3 => ({ x, y, z });
const mix = (a: Point3, b: Point3, t: number): Point3 => p(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, a.z + (b.z - a.z) * t);
const cross = (a: Vector, b: Vector, c: Vector): number => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
export function convexHull(points: Vector[]): Vector[] {
    // Rigid transforms can give equivalent pole vertices a few ulps of noise.
    // Canonical ties prevent zero-length hull edges and unstable vertex ordering.
    const sorted = [...points].sort((a, b) => Math.abs(a.x - b.x) > 1e-10 ? a.x - b.x : a.y - b.y);
    const lower: Vector[] = [], upper: Vector[] = [];
    for (const point of sorted) {
        while (lower.length > 1 && cross(lower[lower.length - 2], lower[lower.length - 1], point) <= 1e-10) lower.pop();
        lower.push(point);
    }
    for (let i = sorted.length - 1; i >= 0; i--) {
        const point = sorted[i];
        while (upper.length > 1 && cross(upper[upper.length - 2], upper[upper.length - 1], point) <= 1e-10) upper.pop();
        upper.push(point);
    }
    lower.pop(); upper.pop();
    return [...lower, ...upper];
}

export const RIG_GESTURES: RigGesture[] = ["ready", "fire", "punch", "reload", "heal", "throw", "punchLeft"];
export const RIG_IDLE: OperatorRigState = {
    rotation: 0, phase: 0, moving: 0, moveAngle: 0, gesture: "ready", gesturePhase: 0,
    helmet: 0, vest: 0, backpack: 0, leftHand: Vec(1.9, -0.55), rightHand: Vec(1.1, 0.65), carry: "unarmed"
};

/** Art and collision share these articulated convex meshes. No directional bitmap frames. */
export function buildOperatorRig(state: OperatorRigState, includeInk = true, includeHulls = true, sink?: OperatorRigSink): RigPart[] {
    const parts: RigPart[] = sink?.begin(state) ?? [];
    let joint: RigTransform = RIG_IDENTITY;
    const setJoint = (transform: RigTransform): void => { joint = transform; sink?.setTransform?.(transform); };
    // Invert the ground-plane projection so continuous model yaw follows the actual crosshair.
    const groundTilt = 0.54, verticalTilt = 0.84;
    const yaw = Math.atan2(Math.sin(state.rotation) / groundTilt, Math.cos(state.rotation));
    const cy = Math.cos(yaw), sy = Math.sin(yaw);
    const bodyScale = 1.15;
    // A shallower character view exposes the face and gear, like the approved art.
    // Vertical framing preserves the former height while the map camera stays unchanged.
    const chestAnchor = 2.55;
    const project = (v: Point3): Vector => Vec((v.x * cy - v.y * sy) * bodyScale, (v.x * sy + v.y * cy) * groundTilt - v.z * verticalTilt + chestAnchor);
    const depth = (v: Point3): number => (v.x * sy + v.y * cy) * verticalTilt + v.z * groundTilt;
    const shade = (color: number, light: number): number => {
        const c = (shift: number): number => Math.min(255, Math.round(((color >> shift) & 255) * light));
        return (c(16) << 16) | (c(8) << 8) | c(0);
    };
    const mesh = (name: string, vertices: Point3[], faces: number[][], color: number, ink = true, normals?: Point3[], convex = false): void => {
        if (sink) { sink.mesh(name, vertices, faces, color, ink, normals, convex); return; }
        if (joint !== RIG_IDENTITY) {
            vertices = vertices.map(v => transformRigPoint(joint, v));
            normals = normals?.map(n => transformRigPoint(joint, n, true));
        }
        const points = vertices.map(project);
        if (!includeInk && convex) {
            parts.push({ name, hull: convexHull(points), surfaces: [], convex: true });
            return;
        }
        const surfaces: RigSurface[] = [];
        const edges = new Map<number, { face: RigSurface; a: Vector; b: Vector; count: number }>();
        for (const face of faces) {
            const pp = face.map(i => points[i]);
            if (cross(pp[0], pp[1], pp[2]) <= 0.000001) continue;
            const a = vertices[face[0]], b = vertices[face[1]], c = vertices[face[2]];
            const u = p(b.x - a.x, b.y - a.y, b.z - a.z), v = p(c.x - a.x, c.y - a.y, c.z - a.z);
            const n = p(u.y * v.z - u.z * v.y, u.z * v.x - u.x * v.z, u.x * v.y - u.y * v.x);
            const len = Math.hypot(n.x, n.y, n.z) || 1;
            const nx = n.x * cy - n.y * sy, ny = n.x * sy + n.y * cy;
            const lightDot = (-nx * 0.45 - ny * 0.3 + n.z * 0.83) / len;
            // Three broad painted tones instead of a metallic/faceted lighting ramp.
            const light = lightDot < 0.08 ? 0.63 : lightDot < 0.65 ? 0.88 : 1.12;
            const surface: RigSurface = { points: pp, depth: face.reduce((s, i) => s + depth(vertices[i]), 0) / face.length, color: shade(color, light) };
            if (includeInk) {
                surface.depths = face.map(i => depth(vertices[i]));
                surface.pigment = color;
                surface.lights = face.map(i => {
                    const nn = normals?.[i];
                    if (!nn) return lightDot;
                    return (-(nn.x * cy - nn.y * sy) * 0.45 - (nn.x * sy + nn.y * cy) * 0.3 + nn.z * 0.83) / (Math.hypot(nn.x, nn.y, nn.z) || 1);
                });
            }
            surfaces.push(surface);
            if (includeInk && ink) for (let i = 0; i < face.length; i++) {
                const a = face[i], b = face[(i + 1) % face.length];
                const key = Math.min(a, b) * vertices.length + Math.max(a, b);
                const edge = edges.get(key);
                if (edge) edge.count++;
                else edges.set(key, { face: surface, a: points[a], b: points[b], count: 1 });
            }
        }
        for (const edge of edges.values()) if (edge.count === 1) {
            const dx = edge.b.x - edge.a.x, dy = edge.b.y - edge.a.y, length = Math.hypot(dx, dy);
            if (length < 0.002) continue;
            // Ink is inset into the visible polygon, never an invisible hitbox margin.
            const center = Vec(edge.face.points.reduce((sum, point) => sum + point.x, 0) / edge.face.points.length,
                edge.face.points.reduce((sum, point) => sum + point.y, 0) / edge.face.points.length);
            const inset = (point: Vector): Vector => {
                const t = Math.min(0.5, 0.045 / (Math.hypot(center.x - point.x, center.y - point.y) || 1));
                return Vec(point.x + (center.x - point.x) * t, point.y + (center.y - point.y) * t);
            };
            (edge.face.ink ??= []).push([edge.a, edge.b, inset(edge.b), inset(edge.a)]);
        }
        parts.push({ name, hull: includeHulls ? convexHull(points) : [], surfaces, convex });
    };
    const ellipsoid = (name: string, center: Point3, size: Point3, color: number, segments = 14, rings = 7, phiEnd = Math.PI): void => {
        if (sink) { sink.ellipsoid(name, center, size, color, segments, rings, phiEnd); return; }
        const vertices: Point3[] = [], faces: number[][] = [];
        for (let r = 0; r <= rings; r++) {
            const phi = phiEnd * r / rings;
            for (let i = 0; i < segments; i++) {
                const theta = 2 * Math.PI * i / segments;
                vertices.push(p(center.x + size.x * Math.sin(phi) * Math.cos(theta), center.y + size.y * Math.sin(phi) * Math.sin(theta), center.z + size.z * Math.cos(phi)));
            }
        }
        for (let r = 0; r < rings; r++) for (let i = 0; i < segments; i++) {
            const j = (i + 1) % segments;
            const a = r * segments + i, b = r * segments + j, c = (r + 1) * segments + j, d = (r + 1) * segments + i;
            faces.push(r === 0 ? [a, c, d] : r === rings - 1 && phiEnd === Math.PI ? [a, b, c] : [a, b, c, d]);
        }
        if (phiEnd !== Math.PI) faces.push(Array.from({ length: segments }, (_, i) => rings * segments + i));
        const normals = includeInk ? vertices.map(v => p((v.x - center.x) / (size.x ** 2), (v.y - center.y) / (size.y ** 2), (v.z - center.z) / (size.z ** 2))) : undefined;
        mesh(name, vertices, faces.map(face => face.reverse().map(i => i < segments ? 0 : phiEnd === Math.PI && i >= rings * segments ? rings * segments : i)), color, true, normals, true);
    };
    const box = (name: string, center: Point3, size: Point3, color: number, bevel = 0.18): void => {
        if (sink) { sink.box(name, center, size, color, bevel); return; }
        const vertices: Point3[] = [];
        const oct = [[-1, -1 + bevel], [-1 + bevel, -1], [1 - bevel, -1], [1, -1 + bevel], [1, 1 - bevel], [1 - bevel, 1], [-1 + bevel, 1], [-1, 1 - bevel]];
        for (const z of [-1, 1]) for (const [x, y] of oct) vertices.push(p(center.x + x * size.x, center.y + y * size.y, center.z + z * size.z));
        const faces = [[7, 6, 5, 4, 3, 2, 1, 0], [8, 9, 10, 11, 12, 13, 14, 15]];
        for (let i = 0; i < 8; i++) faces.push([i, (i + 1) % 8, (i + 1) % 8 + 8, i + 8]);
        mesh(name, vertices, faces, color, true, undefined, true);
    };
    const limb = (name: string, a: Point3, b: Point3, radius: number, color: number): void => {
        if (sink) { sink.limb(name, a, b, radius, color); return; }
        const d = p(b.x - a.x, b.y - a.y, b.z - a.z), length = Math.hypot(d.x, d.y, d.z) || 1;
        d.x /= length; d.y /= length; d.z /= length;
        let u = p(-d.y, d.x, 0), ul = Math.hypot(u.x, u.y);
        if (ul < 0.001) { u = p(1, 0, 0); ul = 1; }
        u.x /= ul; u.y /= ul;
        const v = p(d.y * u.z - d.z * u.y, d.z * u.x - d.x * u.z, d.x * u.y - d.y * u.x);
        const vertices: Point3[] = [], faces: number[][] = [];
        for (const end of [a, b]) for (let i = 0; i < 8; i++) {
            const angle = i * Math.PI / 4;
            vertices.push(p(end.x + radius * (u.x * Math.cos(angle) + v.x * Math.sin(angle)), end.y + radius * (u.y * Math.cos(angle) + v.y * Math.sin(angle)), end.z + radius * (u.z * Math.cos(angle) + v.z * Math.sin(angle))));
        }
        faces.push([7, 6, 5, 4, 3, 2, 1, 0], [8, 9, 10, 11, 12, 13, 14, 15]);
        for (let i = 0; i < 8; i++) faces.push([i, (i + 1) % 8, (i + 1) % 8 + 8, i + 8]);
        mesh(name, vertices, faces, color, true, undefined, true);
    };

    const moving = state.moving;
    const cycle = state.phase * Math.PI * 2;
    const body = operatorBodyPose(state), bounce = body.bounce;
    const movementYaw = Math.atan2(Math.sin(state.moveAngle) / groundTilt, Math.cos(state.moveAngle)) - yaw;
    const outfit = operatorSkin(state.skinId);
    const premium = !!outfit.priceUSD && includeInk;
    const royal = outfit.design === "sovereign", angel = outfit.design === "seraph";
    const metal = royal ? 0xefc868 : outfit.helmet;
    const colors = { armor: outfit.jacket, edge: 0x242932, suit: outfit.jeans, lime: outfit.accent,
        glove: 0x454a42, visor: outfit.visor, skin: 0xe5b083, canvas: 0x8b8853, leather: outfit.boots };
    const seam = (name: string, a: Point3, b: Point3, color = colors.edge, width = 0.025): void => limb(name, a, b, width, color);
    for (const side of [-1, 1]) {
        const stride = operatorFoot(state, side), foot = stride.center;
        const bootJoint = jointTransform(foot, stride.yaw, stride.pitch, 0, p(0, 0, 0));
        const hip = transformRigPoint(body.hips, p(-0.04, side * 0.48, 2.35 + bounce));
        const ankle = transformRigPoint(bootJoint, p(foot.x - 0.10, foot.y, foot.z + 0.61));
        const knee = solveOperatorJoint(hip, ankle, p(Math.cos(stride.yaw), Math.sin(stride.yaw) + side * 0.12, 0), 1.20, 1.16);
        limb(`thigh${side}`, hip, knee, 0.37, colors.suit);
        ellipsoid(`trouserFold${side}`, mix(hip, knee, 0.28), p(0.40, 0.40, 0.38), colors.suit, 12, 6);
        if (outfit.design === "ranger" || outfit.design === "warden" || outfit.design === "dune") box(`cargoPocket${side}`, p(hip.x, hip.y + side * 0.32, hip.z - 0.35), p(0.27, 0.11, 0.27), outfit.accent);
        limb(`shin${side}`, knee, ankle, 0.28, colors.suit);
        ellipsoid(`knee${side}`, p(knee.x + 0.24, knee.y, knee.z), p(0.18, 0.35, 0.32), colors.leather, 12, 6);
        box(`kneeRivet${side}`, p(knee.x + 0.414, knee.y, knee.z + 0.14), p(0.01, 0.15, 0.02), 0xa79577);
        if (premium) {
            limb(`premiumGreave${side}`, mix(knee, ankle, .22), mix(knee, ankle, .83), .345, metal);
            ellipsoid(`premiumKneeShield${side}`, p(knee.x + .32, knee.y, knee.z), p(.21,.41,.39), outfit.accent, 6, 4);
            ellipsoid(`premiumKneeInset${side}`, p(knee.x + .51, knee.y, knee.z), p(.055,.20,.24), royal ? outfit.visor : outfit.helmet, 4, 4);
            seam(`premiumShinInlay${side}`, p(knee.x+.34,knee.y,knee.z-.20), p(ankle.x+.30,ankle.y,ankle.z+.12), outfit.accent, .075);
        }
        // Rounded toe, heel, high ankle and a separate outsole make an actual boot.
        setJoint(bootJoint);
        ellipsoid(`boot${side}`, p(foot.x + 0.20, foot.y, foot.z), p(0.59, 0.35, 0.24), colors.leather, 16, 6);
        ellipsoid(`bootSole${side}`, p(foot.x + 0.20, foot.y, foot.z - 0.14), p(0.61, 0.36, 0.085), colors.edge, 16, 4);
        ellipsoid(`bootToe${side}`, p(foot.x + 0.54, foot.y, foot.z + 0.035), p(0.27, 0.33, 0.19), colors.leather, 12, 5);
        limb(`bootAnkle${side}`, p(foot.x - 0.10, foot.y, foot.z + 0.10), p(foot.x - 0.10, foot.y, foot.z + 0.71), 0.30, colors.leather);
        ellipsoid(`bootCuff${side}`, p(foot.x - 0.10, foot.y, foot.z + 0.69), p(0.32, 0.32, 0.10), outfit.accent, 12, 4);
        if (premium) {
            box(`premiumSabatons${side}`,p(foot.x+.33,foot.y,foot.z+.17),p(.45,.38,.105),metal,.38);
            box(`premiumToeTrim${side}`,p(foot.x+.66,foot.y,foot.z+.16),p(.09,.39,.11),outfit.accent,.25);
        }
        if (outfit.design === "glacier") box(`bootGuard${side}`, p(foot.x + 0.22, foot.y, foot.z + 0.46), p(0.10, 0.32, 0.26), outfit.jacket);
        for (let row = 0; row < 3; row++) {
            const z = foot.z + 0.28 + row * 0.115;
            seam(`bootLaceA${side}_${row}`, p(foot.x + 0.19, foot.y - 0.16, z), p(foot.x + 0.20, foot.y + 0.16, z + 0.09), 0xc2ac86, 0.021);
            seam(`bootLaceB${side}_${row}`, p(foot.x + 0.20, foot.y + 0.16, z), p(foot.x + 0.19, foot.y - 0.16, z + 0.09), 0xc2ac86, 0.021);
        }
        setJoint(RIG_IDENTITY);
    }
    setJoint(body.hips);
    ellipsoid("pelvis", p(0, 0, 2.35 + bounce), p(0.51, 0.75, 0.39), colors.suit);
    setJoint(body.chest);
    ellipsoid("torso", p(-0.02, 0, 3.16 + bounce), p(0.60, 0.80, outfit.design === "volt" ? 0.73 : 0.83), colors.armor, 16, 8);
    if (outfit.priceUSD && includeInk) {
        box("premiumChest", p(0.52, 0, 3.35 + bounce), p(0.13, 0.65, 0.46), outfit.helmet, 0.28);
        for (const side of [-1, 1]) {
            seam(`premiumChestInlay${side}`, p(0.67, side * 0.54, 3.65 + bounce), p(0.70, 0, 3.28 + bounce), outfit.accent, 0.065);
            box(`premiumHipPlate${side}`, p(0.05, side * 0.62, 2.50 + bounce), p(0.44, 0.15, 0.31), outfit.helmet, 0.25);
            if (outfit.design === "seraph") for (let i = 0; i < 3; i++) {
                ellipsoid(`seraphBackFeather${side}_${i}`, p(-0.69 - i * 0.04, side * (0.35 + i * 0.18), 3.50 - i * 0.12 + bounce), p(0.14, 0.20, 0.56), i % 2 ? outfit.accent : outfit.helmet, 4, 4);
            }
            if (outfit.design === "sovereign") {
                box(`sovereignMantle${side}`, p(-0.68, side * 0.38, 2.83 + bounce), p(0.07, 0.31, 0.68), outfit.jeans, 0.18);
                seam(`sovereignMantleTrim${side}`, p(-0.77, side * 0.66, 3.4 + bounce), p(-0.77, side * 0.66, 2.16 + bounce), outfit.accent, 0.055);
            }
        }
        if (outfit.design === "spectre") box("spectreMantle", p(-0.66, 0.32, 3.47 + bounce), p(0.12, 0.51, 0.41), outfit.helmet, 0.22);
        if (outfit.design === "sovereign") ellipsoid("sovereignChestGem", p(0.73, 0, 3.43 + bounce), p(0.085, 0.19, 0.25), outfit.visor, 4, 3);
    }
    if (outfit.design === "glacier" || outfit.design === "tide") {
        ellipsoid("jacketCollar", p(-0.04, 0, 3.90 + bounce), p(0.55, 0.61, 0.26), outfit.accent, 14, 6);
    }
    if (outfit.design === "ember" || outfit.design === "orchid" || outfit.design === "sable") {
        box("bomberHem", p(0, 0, 2.66 + bounce), p(0.56, 0.77, 0.18), colors.edge);
        seam("bomberZip", p(0.51, -0.18, 2.87 + bounce), p(0.39, 0.25, 3.76 + bounce), outfit.accent, 0.043);
    } else seam("jacketZip", p(0.59, 0, 2.85 + bounce), p(0.44, 0, 3.77 + bounce), colors.edge, 0.034);
    if (outfit.design === "ranger" || outfit.design === "warden" || outfit.design === "dune") for (const side of [-1, 1]) {
        box(`fieldPocket${side}`, p(0.57, side * 0.39, 3.34 + bounce), p(0.08, 0.23, 0.23), outfit.accent);
    }
    setJoint(body.hips);
    box("belt", p(0, 0, 2.58 + bounce), p(0.60, 0.77, 0.13), colors.glove);
    box("buckle", p(0.63, 0, 2.58 + bounce), p(0.06, 0.19, 0.12), 0xbaa979);
    box("buckleInset", p(0.695, 0, 2.58 + bounce), p(0.012, 0.115, 0.065), colors.edge);
    for (const side of [-1, 1]) {
        setJoint(body.chest);
        limb(`strap${side}`, p(0.27, side * 0.54, 3.83 + bounce), p(0.58, side * 0.45, 2.70 + bounce), 0.095, colors.glove);
        setJoint(body.hips);
        box(`beltPouch${side}`, p(0.43, side * 0.57, 2.60 + bounce), p(0.23, 0.17, 0.24), colors.canvas, 0.32);
        box(`beltFlap${side}`, p(0.66, side * 0.57, 2.72 + bounce), p(0.022, 0.16, 0.08), 0xa4a16d);
    }
    setJoint(body.chest);
    if (premium) {
        const level=Math.min(3,state.vest), front=.76+level*.035;
        // The outfit and real equipment form one armour set. Tier marks still
        // communicate the equipped level; collision always uses the base rig.
        box("premiumEquipmentCuirass",p(.53,0,3.28+bounce),p(.24,.78+level*.025,.65),metal,.42);
        for(const side of [-1,1]) {
            seam(`premiumEquipmentChestChevron${side}`,p(front,side*.66,3.74+bounce),p(front+.10,0,3.22+bounce),outfit.accent,.115);
            box(`premiumEquipmentBeltPlate${side}`,p(.60,side*.48,2.75+bounce),p(.18,.25,.22),metal,.35);
            for(let mark=0;mark<level;mark++)box(`premiumEquipmentVestRank${side}_${mark}`,p(front+.015,side*(.38+mark*.12),3.05+bounce),p(.028,.037,.09),outfit.accent);
        }
        ellipsoid("premiumEquipmentChestEmblem",p(front+.18,0,3.53+bounce),p(.08,royal?.26:.18,royal?.31:.21),outfit.visor,4,4);
        if(royal)for(const side of [-1,1]) {
            // Long split brocade panels remain visible below every backpack.
            box(`sovereignRoyalSkirt${side}`,p(-.61,side*.67,2.24+bounce),p(.14,.35,.77),0x663789,.3);
            seam(`sovereignRoyalHem${side}`,p(-.80,side*.98,2.94+bounce),p(-.80,side*.98,1.5+bounce),outfit.accent,.085);
            ellipsoid(`sovereignHemSeal${side}`,p(-.78,side*.68,1.91+bounce),p(.04,.17,.22),outfit.accent,4,4);
        }
    }
    if (state.vest > 0 && !premium) {
        const level = Math.min(3, state.vest);
        const color = [0, 0xc0ae80, 0x718b64, 0x344451][level];
        const width = 0.69 + level * 0.055;
        box("vestPlate", p(0.53, 0, 3.22 + bounce), p(0.22, width, 0.61), color, 0.30);
        box("vestBack", p(-0.49, 0, 3.26 + bounce), p(0.18, width, 0.60), color, 0.30);
        for (const side of [-1, 1]) {
            box(`vestShoulder${side}`, p(0, side * 0.62, 3.78 + bounce), p(0.53, 0.19, 0.14), color);
            box(`vestSide${side}`, p(0, side * width, 3.19 + bounce), p(0.52, 0.10, 0.34), color);
            if (level >= 2) {
                box(`vestPouch${side}`, p(0.81, side * 0.34, 3.04 + bounce), p(0.12, 0.24, 0.25), 0x475841);
                box(`vestFlap${side}`, p(0.94, side * 0.34, 3.23 + bounce), p(0.025, 0.24, 0.06), 0xa5b790);
            }
            if (level === 3) box(`vestPauldron${side}`, p(0.04, side * 0.92, 3.79 + bounce), p(0.36, 0.25, 0.24), 0x607581);
        }
        if (level === 3) {
            box("vestHeavyChest", p(0.79, 0, 3.53 + bounce), p(0.08, 0.66, 0.25), 0x8fabb2);
            box("vestHeavyBack", p(-0.69, 0, 3.50 + bounce), p(0.04, 0.65, 0.26), 0x8fabb2);
        }
    }
    if (premium && state.backpack > 0) {
        const level=Math.min(3,state.backpack), x=-.97-level*.08, z=3.24+bounce;
        box("premiumEquipmentBackpack",p(x,0,z),p(.36,.58+level*.055,.62+level*.055),metal,.45);
        box("premiumEquipmentPackInset",p(x-.37,0,z),p(.045,.43,.44),outfit.jeans,.35);
        ellipsoid("premiumEquipmentPackEmblem",p(x-.44,0,z+.10),p(.045,.27,.30),outfit.accent,angel?6:4,4);
        ellipsoid("premiumEquipmentPackGem",p(x-.49,0,z+.10),p(.025,.13,.16),outfit.visor,4,4);
        for(const side of [-1,1]) {
            box(`premiumEquipmentPackRail${side}`,p(x-.34,side*.52,z),p(.09,.09,.58),outfit.accent,.25);
            if(angel)for(let feather=0;feather<3;feather++) {
                ellipsoid(`premiumEquipmentWing${side}_${feather}`,p(x-.23-feather*.06,side*(.64+feather*.17),z+.27-feather*.23),p(.20,.27,.58),feather===1?outfit.accent:outfit.helmet,4,4);
            }
            if(!angel)box(`premiumEquipmentPackPod${side}`,p(x,side*.72,z-.10),p(.28,.16,.38),royal?outfit.accent:outfit.helmet,.42);
        }
        for(let mark=0;mark<level;mark++)box(`premiumEquipmentPackRank${mark}`,p(x-.43,(mark-(level-1)/2)*.18,z-.40),p(.025,.06,.025),outfit.accent);
    }
    if (state.backpack > 0 && !premium) {
        const level = Math.min(3, state.backpack);
        const packX = -0.86 - level * 0.22;
        const packZ = 3.12 + bounce + Math.sin(cycle) * moving * 0.025;
        const packColor = [0, 0xb39a70, 0x6f8154, 0x3e505b][level];
        const packHeight = 0.43 + level * 0.13;
        box("backpack", p(-0.88 - level * 0.08, 0, packZ), p(0.26 + level * 0.08, 0.43 + level * 0.12, packHeight), packColor, 0.40);
        ellipsoid("packLid", p(-0.94 - level * 0.06, 0, packZ + packHeight - 0.08), p(0.30 + level * 0.07, 0.42 + level * 0.12, 0.20), packColor, 14, 6);
        box("packFlap", p(packX - 0.16, 0, packZ + 0.23), p(0.055, 0.34 + level * 0.10, 0.23), packColor, 0.32);
        if (level === 1) {
            box("packFrontPocket", p(packX - 0.23, 0, packZ - 0.20), p(0.10, 0.35, 0.23), 0x8c795b);
            box("packClasp", p(packX - 0.22, 0, packZ + 0.11), p(0.04, 0.09, 0.10), 0xdbcead);
        }
        if (level >= 2) {
        for (const side of [-1, 1]) {
            box(`packPocket${side}`, p(packX - 0.19, side * 0.35, packZ - 0.29), p(0.14, 0.27, 0.31), packColor, 0.40);
            box(`packPocketFlap${side}`, p(packX - 0.34, side * 0.35, packZ - 0.08), p(0.025, 0.25, 0.11), level === 2 ? 0xa2ad7a : 0x8cabb4, 0.30);
            box(`packStrap${side}`, p(packX - 0.25, side * 0.38, packZ + 0.34), p(0.023, 0.067, 0.35), 0x363842);
            box(`packBuckle${side}`, p(packX - 0.28, side * 0.38, packZ + 0.32), p(0.02, 0.106, 0.10), 0xc5b38d);
            box(`packBuckleHole${side}`, p(packX - 0.305, side * 0.38, packZ + 0.32), p(0.009, 0.061, 0.055), colors.edge);
            seam(`packStitch${side}`, p(packX - 0.386, side * 0.35 - 0.20, packZ - 0.45), p(packX - 0.386, side * 0.35 + 0.20, packZ - 0.45), 0xb6b17b, 0.016);
        }
        limb("bedroll", p(-1.10, -0.88, packZ + packHeight + 0.16), p(-1.10, 0.88, packZ + packHeight + 0.16), 0.24, level === 2 ? 0xa3a171 : 0x829ca5);
        for (const side of [-1, 1]) {
            ellipsoid(`rollEnd${side}`, p(-1.10, side * 0.89, packZ + packHeight + 0.16), p(0.21, 0.022, 0.21), colors.edge, 12, 5);
            ellipsoid(`rollInner${side}`, p(-1.10, side * 0.913, packZ + packHeight + 0.16), p(0.14, 0.012, 0.14), 0xb4b89a, 12, 5);
        }
        }
        if (level === 3) {
            for (const side of [-1, 1]) box(`packSidePod${side}`, p(-1.05, side * 0.88, packZ - 0.08), p(0.30, 0.20, 0.45), 0x728d96);
            box("packRadio", p(-1.09, 0.91, packZ + 0.53), p(0.25, 0.18, 0.27), colors.edge);
            limb("packAntenna", p(-1.09, 0.91, packZ + 0.76), p(-1.09, 0.91, packZ + 1.28), 0.035, 0xa0bbc0);
        }
    }

    // Hand goals are the equipped weapon's grip positions in screen space. Solve elbows between shoulder and wrist.
    setJoint(RIG_IDENTITY);
    const gesture = Math.sin(Math.PI * Math.min(1, state.gesturePhase));
    // Reuse the replicated gait and movement blend; actions take priority over
    // free-hand swing. Held grips and the weapon/muzzle transform stay anchored.
    const actionBlend = state.gesture === "ready" ? 0
        : Math.max(0, Math.min(1, state.gesturePhase / 0.12, (1 - state.gesturePhase) / 0.18));
    const walkArms = state.armMotion === false ? 0 : moving * (1 - actionBlend * (state.gesture === "fire" ? 0.82 : 1));
    const armBeat = Math.sin(cycle) * walkArms;
    const compactArms = state.compactArms === true;
    const stepX = Math.cos(movementYaw), stepY = Math.sin(movementYaw);
    for (const side of [-1, 1]) {
        const goal = side < 0 ? state.leftHand : state.rightHand;
        const screen = Vec.rotate(goal, state.rotation);
        let z = 3.12;
        let gx = screen.x / bodyScale, gy = screen.y;
        if (state.gesture === "fire") { gx -= Math.cos(state.rotation) * gesture * 0.16; gy -= Math.sin(state.rotation) * gesture * 0.16; }
        if ((state.gesture === "punch" && side > 0) || (state.gesture === "punchLeft" && side < 0)) {
            const extension = state.gesturePhase < 0.2 ? state.gesturePhase / 0.2 : (1 - state.gesturePhase) / 0.8;
            gx += Math.cos(state.rotation) * extension * 1.1; gy += Math.sin(state.rotation) * extension * 1.1;
        }
        if (state.gesture === "reload" && side < 0) { gx *= 1 - gesture * 0.65; gy += gesture * 0.8; z -= gesture * 0.5; }
        if (state.gesture === "heal") { gx *= 1 - gesture * 0.6; gy -= gesture * 0.35; z += gesture * 0.3; }
        if (state.gesture === "throw" && side > 0) { gx -= Math.cos(state.rotation) * gesture * 1.5; gy -= Math.sin(state.rotation) * gesture * 1.5; z += gesture * 0.6; }
        const groundY = (gy + z * verticalTilt - chestAnchor) / groundTilt;
        const wrist = p(gx * cy + groundY * sy, -gx * sy + groundY * cy, z);
        const swing = -side * armBeat;
        const free = state.carry === "unarmed" || (state.carry === "oneHanded" && side < 0);
        const freeBlend = free ? walkArms * (1 - actionBlend) : 0;
        if (freeBlend > 0) {
            // Lower the relaxed hand, then swing opposite its corresponding foot.
            // Mixing with movement weight smoothly restores the ready stance on stop.
            const freeSwing = swing * (1 - actionBlend);
            wrist.x += (0.14 - wrist.x) * freeBlend + stepX * freeSwing * 0.62;
            wrist.y += (side * 1.02 - wrist.y) * freeBlend + stepY * freeSwing * 0.62;
            wrist.z += (2.12 - wrist.z) * freeBlend + Math.abs(freeSwing) * 0.10;
        }
        const shoulder = transformRigPoint(body.chest, p(0.01, side * 0.82, 3.62 + bounce));
        const carried = state.carry === "twoHanded" ? walkArms : 0;
        // Preserve a bent running elbow even when the flat weapon sprite's
        // foreshortened grips exceed the neutral 3D arm reach at some aim angles.
        const reach = 0.999 - carried * (0.119 + (1 - Math.cos(cycle * 2)) * 0.02);
        const elbow = solveOperatorJoint(shoulder, wrist,
            p(stepX * swing * 0.30 - 0.15 - carried * 0.35, side * (0.70 - carried * 0.18) + stepY * swing * 0.30, -0.75), 1.02, 1.04, reach);
        const cuff = mix(shoulder, elbow, 0.72);
        const sleeveColor = outfit.design === "tide" || outfit.design === "sable" ? outfit.accent : colors.armor;
        limb(`upperArm${side}`, shoulder, cuff, 0.32, sleeveColor);
        const shoulderColor = outfit.design === "volt" ? colors.edge
            : ["ranger", "glacier", "tide", "sable", "warden", "orchid"].includes(outfit.design) ? outfit.accent : colors.armor;
        ellipsoid(`shoulder${side}`, shoulder, p(0.34, 0.37, 0.37), shoulderColor, compactArms ? 12 : 14, compactArms ? 6 : 7);
        if (outfit.priceUSD && includeInk) {
            for (let i = 0; i < (outfit.design === "spectre" ? 1 : 3); i++) {
                ellipsoid(`premiumPauldron${side}_${i}`, p(shoulder.x - i * 0.04, shoulder.y + side * i * 0.08, shoulder.z + 0.16 - i * 0.14),
                    p(0.53 - i * 0.025, 0.54, 0.23), i % 2 ? metal : outfit.accent, 8, 4);
            }
            limb(`premiumGauntlet${side}`, mix(elbow, wrist, 0.35), mix(elbow, wrist, 0.80), 0.31, metal);
            seam(`premiumGauntletInlay${side}`, p(elbow.x + 0.23, elbow.y, elbow.z), p(wrist.x + 0.20, wrist.y, wrist.z + 0.06), outfit.accent, 0.045);
        }
        const rolled = outfit.design === "nova" || outfit.design === "ranger" || outfit.design === "dune";
        ellipsoid(`sleeveRoll${side}`, cuff, p(0.34, 0.34, 0.20), rolled ? outfit.accent : sleeveColor, compactArms ? 10 : 12, compactArms ? 5 : 6);
        limb(`bareElbow${side}`, cuff, elbow, 0.265, rolled ? colors.skin : sleeveColor);
        ellipsoid(`elbow${side}`, elbow, p(0.27, 0.27, 0.26), rolled ? colors.skin : sleeveColor, compactArms ? 10 : 12, compactArms ? 5 : 6);
        limb(`forearm${side}`, elbow, mix(elbow, wrist, 0.87), 0.23, rolled ? colors.skin : sleeveColor);
        limb(`gloveCuff${side}`, mix(elbow, wrist, 0.82), mix(elbow, wrist, 0.96), 0.26, colors.edge);
        // Readable palm, back pad, four curled fingers and an opposing thumb.
        // These pieces also enter the weapon-overlap layer, exposing both grips.
        ellipsoid(`glove${side}`, wrist, p(0.32, 0.29, 0.24), 0x637065, compactArms ? 12 : 14, compactArms ? 6 : 7);
        box(`gloveBack${side}`, p(wrist.x - 0.035, wrist.y, wrist.z + 0.20), p(0.205, 0.21, 0.065), 0x8f9b83, 0.34);
        ellipsoid(`gloveThumb${side}`, p(wrist.x + 0.14, wrist.y - side * 0.24, wrist.z + 0.025), p(0.21, 0.13, 0.145), colors.skin, compactArms ? 8 : 10, compactArms ? 4 : 5);
        for (let finger = 0; finger < 4; finger++) {
            ellipsoid(`gloveFinger${side}_${finger}`, p(wrist.x + 0.22 - finger * 0.13, wrist.y + side * 0.20, wrist.z + 0.125), p(0.055, 0.15, 0.115), colors.skin, compactArms ? 6 : 8, compactArms ? 3 : 4);
        }
    }
    setJoint(body.head);
    ellipsoid("neck", p(0.04, 0, 3.97 + bounce), p(0.33, 0.36, 0.28), colors.skin, 12, 6);
    ellipsoid("face", p(0.09, 0, 4.43 + bounce), p(0.72, 0.74, 0.59), colors.skin, 18, 8);
    ellipsoid("chin", p(0.42, 0, 4.17 + bounce), p(0.40, 0.52, 0.20), colors.skin, 14, 6);
    const lowHelmet = outfit.design === "sable";
    const angularHelmet = ["ember", "glacier", "volt", "warden", "orchid"].includes(outfit.design);
    ellipsoid("helmetShell", p(-0.04, 0, 4.76 + bounce), p(1.00, 1.00, lowHelmet ? 0.80 : outfit.design === "volt" ? 0.85 : 0.92), outfit.helmet,
        outfit.design === "warden" ? 8 : angularHelmet ? 12 : 24, 10, 1.63);
    ellipsoid("helmetRim", p(0.01, 0, 4.76 + bounce), p(1.07, 1.045, 0.085), colors.edge, 24, 4);
    if (outfit.priceUSD && includeInk) {
        for (const side of [-1, 1]) {
            ellipsoid(`premiumHelmetTrim${side}`, p(-0.10, side * 0.74, 5.10 + bounce), p(0.81, 0.09, 0.31), outfit.accent, 12, 4);
            const z = 5.46 + bounce;
            const vertices = [p(-0.82, side * 0.61, z), p(0.20, side * 0.61, z),
                p(outfit.design === "spectre" ? -1.15 : -0.30, side * 0.69, z + 0.65),
                p(-0.37, side * 0.78, z + 0.09)];
            mesh(`premiumCrest${side}`, vertices, [[0,1,2],[0,3,1],[0,2,3],[1,3,2]], outfit.accent, true, undefined, true);
        }
        if (outfit.design === "sovereign") {
            ellipsoid("sovereignCrownPoint", p(0.60, 0, 5.65 + bounce), p(0.13, 0.16, 0.54), outfit.accent, 4, 4);
            ellipsoid("sovereignCrownGem", p(0.99, 0, 5.22 + bounce), p(0.07, 0.16, 0.23), outfit.visor, 4, 3);
        }
    }
    const visorPatch = (name: string, from: number, to: number, width: number, radius: number, color: number): void => {
        const vertices: Point3[] = [], faces: number[][] = [];
        for (let r = 0; r <= 3; r++) for (let i = 0; i <= 8; i++) {
            const phi = from + (to - from) * r / 3, theta = -width + width * 2 * i / 8;
            vertices.push(p(0.07 + radius * Math.sin(phi) * Math.cos(theta), radius * Math.sin(phi) * Math.sin(theta), 4.87 + bounce + 0.90 * Math.cos(phi)));
        }
        for (let r = 0; r < 3; r++) for (let i = 0; i < 8; i++) { const a = r * 9 + i; faces.push([a, a + 9, a + 10, a + 1]); }
        mesh(name, vertices, faces, color);
    };
    visorPatch("visorFrame", 1.30, 2.10, 1.0, 1.055, colors.edge);
    const narrowVisor = lowHelmet || outfit.design === "volt";
    visorPatch("visor", narrowVisor ? 1.52 : 1.40, narrowVisor ? 1.87 : 1.97, outfit.design === "tide" ? 0.97 : 0.91, 1.077, colors.visor);
    visorPatch("visorGlint", narrowVisor ? 1.53 : 1.41, narrowVisor ? 1.61 : 1.52, 0.85, 1.086, 0xbeffef);
    if (outfit.design === "ranger" || outfit.design === "dune") {
        box("visorGoggleBridge", p(1.155, 0, 4.76 + bounce), p(0.045, 0.075, 0.26), colors.edge);
        box("helmetScoutPeak", p(0.82, 0, 4.99 + bounce), p(outfit.design === "dune" ? 0.35 : 0.48, 0.82, 0.075), outfit.helmet, 0.36);
    }
    if (outfit.design === "ember") {
        box("helmetRidge", p(-0.10, 0, 5.62 + bounce), p(0.57, 0.14, 0.13), colors.edge);
        for (const side of [-1, 1]) box(`helmetCheek${side}`, p(0.47, side * 0.68, 4.34 + bounce), p(0.32, 0.18, 0.22), outfit.helmet, 0.36);
    }
    if (outfit.design === "volt" || outfit.design === "warden") box("cosmeticBrow", p(0.96, 0, 5.00 + bounce), p(0.22, 0.84, 0.07), outfit.accent, 0.25);
    if (outfit.design === "tide") box("marineChinPanel", p(0.78, 0, 4.28 + bounce), p(0.16, 0.52, 0.17), outfit.jacket, 0.30);
    const stripeVertices: Point3[] = [], stripeFaces: number[][] = [];
    for (let i = 0; i <= 10; i++) {
        const angle = -1.5 + i * 0.30;
        for (const side of [-1, 1]) stripeVertices.push(p(-0.04 + Math.sin(angle) * 1.018, side * 0.115, 4.76 + bounce + Math.cos(angle) * 0.94));
        if (i < 10) stripeFaces.push([i * 2, i * 2 + 2, i * 2 + 3, i * 2 + 1]);
    }
    if (outfit.design === "glacier" || outfit.design === "sable" || outfit.design === "orchid") {
        for (const side of [-1, 1]) mesh(`crownRail${side}`, stripeVertices.map(v => p(v.x * 0.94, v.y * 0.60 + side * 0.37, v.z - (lowHelmet ? 0.15 : 0.04))), stripeFaces,
            outfit.design === "glacier" ? outfit.jacket : outfit.accent);
    } else if (outfit.design === "nova") mesh("crownStripe", stripeVertices, stripeFaces, colors.lime);
    for (const side of [-1, 1]) {
        ellipsoid(`ear${side}`, p(-0.11, side * 0.88, 4.48 + bounce), p(0.28, 0.17, 0.36), colors.edge, 14, 6);
        ellipsoid(`earCup${side}`, p(-0.11, side * 1.015, 4.48 + bounce), p(0.22, 0.055, 0.28), colors.glove, 14, 6);
        seam(`chinStrap${side}`, p(-0.08, side * 0.71, 4.24 + bounce), p(0.53, side * 0.36, 4.02 + bounce), colors.glove, 0.062);
    }
    if (premium) {
        const level=Math.min(3,state.helmet);
        ellipsoid("premiumHelmetCirclet",p(-.015,0,5.17+bounce),p(1.02,1.055,.13),outfit.accent,24,4);
        for(const side of [-1,1]) {
            box(`premiumHelmetCheek${side}`,p(.57,side*.78,4.35+bounce),p(.40,.19,.30),metal,.48);
            ellipsoid(`premiumHelmetTemple${side}`,p(-.12,side*1.045,4.64+bounce),p(.33,.10,.38),outfit.accent,12,6);
            ellipsoid(`premiumHelmetTempleInset${side}`,p(-.12,side*1.15,4.64+bounce),p(.19,.025,.23),outfit.visor,8,4);
            for(let mark=0;mark<level;mark++)box(`premiumEquipmentHelmetRank${side}_${mark}`,p(-.48+mark*.17,side*1.10,4.91+bounce),p(.052,.026,.07),outfit.accent);
            seam(`premiumHelmetFaceTrim${side}`,p(1.03,side*.65,4.52+bounce),p(.97,side*.38,4.15+bounce),outfit.accent,.085);
        }
        box("premiumHelmetChin",p(.86,0,4.13+bounce),p(.16,.44,.16),metal,.45);
        if(royal)for(let point=0;point<5;point++) {
            const angle=-1.28+point*.64,x=Math.cos(angle)*.94,y=Math.sin(angle)*.94;
            const crown=[p(x-.11,y-.10,5.22+bounce),p(x+.11,y+.10,5.22+bounce),p(x*.82,y*.82,5.98+bounce),p(x-.19,y+.09,5.32+bounce)];
            mesh(`premiumHelmetCrown${point}`,crown,[[0,1,2],[0,3,1],[0,2,3],[1,3,2]],outfit.accent,true,undefined,true);
        }
        if(angel)for(const side of [-1,1]) {
            seam(`premiumHelmetDiadem${side}`,p(.92,side*.63,5.28+bounce),p(1.08,0,5.03+bounce),outfit.accent,.12);
        }
    }
    if (state.helmet > 0 && !premium) {
        const level = Math.min(3, state.helmet);
        const color = [0, 0xb8aa82, 0x718968, 0x435568][level];
        // A real over-shell, visibly larger than the cosmetic base helmet from every yaw.
        ellipsoid("helmetEquipmentShell", p(-0.09, 0, 4.85 + bounce), p(1.07 + level * 0.035, 1.05 + level * 0.035, 0.99), color, level === 3 ? 12 : 20, 8, 1.50);
        ellipsoid("helmetEquipmentRim", p(0.01, 0, 4.92 + bounce), p(1.15 + level * 0.025, 1.13 + level * 0.025, 0.09), level === 1 ? 0x7f775b : colors.edge, 20, 4);
        if (level >= 2) for (const side of [-1, 1]) {
            box(`helmetEarGuard${side}`, p(-0.12, side * 1.08, 4.53 + bounce), p(0.38, 0.18, 0.40), color, 0.40);
            box(`helmetRail${side}`, p(-0.10, side * 1.20, 4.75 + bounce), p(0.33, 0.045, 0.085), 0xb0c1a0);
        }
        if (level === 3) {
            for (const side of [-1, 1]) box(`helmetJawGuard${side}`, p(0.57, side * 0.71, 4.19 + bounce), p(0.46, 0.20, 0.25), 0x7f99ac, 0.45);
            box("helmetBrow", p(1.02, 0, 5.02 + bounce), p(0.21, 0.76, 0.18), 0x8babbc, 0.40);
            box("helmetSensor", p(1.24, 0, 5.03 + bounce), p(0.018, 0.18, 0.075), 0x67eddd);
        }
    }
    return parts;
}
